import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSyncAlt, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { Button, Modal, Table, Spinner, Form } from "react-bootstrap";
import { REACT_APP_API_URL } from "../../../../../env";

const CURRENT_USER_ID = 1;
const API = `${REACT_APP_API_URL}api/tax-config`;

const TaxTypeMaster = () => {
  const [taxTypes, setTaxTypes] = useState([]);
  const [listLoad, setListLoad] = useState(false);
  const [listErr, setListErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [taxTypeName, setTaxTypeName] = useState("");
  const [formLoad, setFormLoad] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);

  const loadTaxTypes = async () => {
    try {
      setListLoad(true);
      setListErr(null);
      const res = await axios.get(`${API}/tax-types-master`);
      setTaxTypes(res.data.data || []);
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not load tax types.");
    } finally {
      setListLoad(false);
    }
  };

  useEffect(() => {
    loadTaxTypes();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setTaxTypeName("");
    setFormErr(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.TaxTypeId);
    setTaxTypeName(row.TaxType);
    setFormErr(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    const name = taxTypeName.trim();
    if (!name) return setFormErr("Tax type name is required");

    try {
      setFormLoad(true);
      setFormErr(null);
      if (editingId) {
        await axios.put(`${API}/tax-types-master/${editingId}`, {
          taxType: name,
          updatedBy: CURRENT_USER_ID
        });
      } else {
        await axios.post(`${API}/tax-types-master`, {
          taxType: name,
          createdBy: CURRENT_USER_ID
        });
      }
      setShowForm(false);
      loadTaxTypes();
    } catch (err) {
      setFormErr(err.response?.data?.message || "Could not save tax type.");
    } finally {
      setFormLoad(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setDeleteLoad(true);
      await axios.delete(`${API}/tax-types-master/${deleteId}`, {
        params: { updatedBy: CURRENT_USER_ID }
      });
      setDeleteId(null);
      loadTaxTypes();
    } catch (err) {
      setListErr(err.response?.data?.message || "Could not delete tax type.");
      setDeleteId(null);
    } finally {
      setDeleteLoad(false);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h3 className="mb-0">Tax Type Master</h3>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={loadTaxTypes} disabled={listLoad}>
              <FaSyncAlt className={listLoad ? "fa-spin" : ""} /> Refresh
            </Button>
            <Button variant="primary" onClick={openAdd}>
              <FaPlus className="me-1" /> Add Tax Type
            </Button>
          </div>
        </div>

        {listErr && <div className="alert alert-danger py-2">{listErr}</div>}

        <div className="table-responsive">
          <Table bordered hover className="align-middle">
            <thead className="text-center">
              <tr>
                <th>#</th>
                <th>Tax Type</th>
                <th>Created On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {listLoad ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : taxTypes.length > 0 ? (
                taxTypes.map((row, index) => (
                  <tr key={row.TaxTypeId}>
                    <td className="text-center">{index + 1}</td>
                    <td>{row.TaxType}</td>
                    <td className="text-center">{row.CreatedOn || "-"}</td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-1">
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(row)} title="Edit">
                          <FaEdit />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setDeleteId(row.TaxTypeId)}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No tax types found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Tax Type" : "Add Tax Type"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Tax Type Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. GST, Luxury Tax, Service Tax"
              value={taxTypeName}
              onChange={(e) => setTaxTypeName(e.target.value)}
              disabled={formLoad}
            />
          </Form.Group>
          {formErr && <div className="alert alert-danger py-2 mt-3 mb-0">{formErr}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={formLoad}>
            Cancel
          </Button>
          <Button variant="dark" onClick={handleSave} disabled={formLoad}>
            {formLoad ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />Saving...
              </>
            ) : editingId ? (
              "Update"
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteId} onHide={() => setDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Tax Type?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This tax type will be deleted. This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteId(null)} disabled={deleteLoad}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoad}>
            {deleteLoad ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaxTypeMaster;
